import React from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Grid } from '@atlaskit/primitives/compiled/grid';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { messages } from '../../messages';
import { LinkCard } from './LinkCard';
import { LinkIconStack } from './LinkIconStack';

const DEFAULT_LINKS_TO_DISPLAY_WHEN_COLLAPSED = 2;
const MAX_LINKS_TO_DISPLAY = 10;

interface RelatedLinksProps {
	links: string[];
	singleColumnLayout?: boolean;
	isExpanded: boolean;
	onExpandToggle: (event: React.MouseEvent<Element, MouseEvent>) => void;
	onResourceClick: ({
		event,
		resourceUrl,
		location,
	}: {
		event: React.MouseEvent | React.KeyboardEvent;
		resourceUrl: string;
		location: string;
	}) => void;
}

const styles = cssMap({
	relatedLinksGrid: {
		marginTop: token('space.050'),
	},
	singleColumnLayout: {
		gridTemplateColumns: '1fr',
	},
	dualColumnLayout: {
		gridTemplateColumns: '1fr 1fr',
	},
	relatedLinksTitle: {
		height: '30px',
	},
});

export const RelatedLinks = ({
	links,
	isExpanded,
	onExpandToggle,
	onResourceClick,
	singleColumnLayout = false,
}: RelatedLinksProps): React.JSX.Element => {
	const linksToDisplayWhenCollapsed = singleColumnLayout
		? 1
		: DEFAULT_LINKS_TO_DISPLAY_WHEN_COLLAPSED;
	const truncatedLinks = links.slice(0, MAX_LINKS_TO_DISPLAY);
	const linksToDisplay = isExpanded
		? truncatedLinks
		: truncatedLinks.slice(0, linksToDisplayWhenCollapsed);

	return (
		<>
			<Inline spread="space-between" alignBlock="center" xcss={styles.relatedLinksTitle}>
				<Text color="color.text.subtle" weight="medium">
					<FormattedMessage {...messages.related_links_title} />
				</Text>
				{links.length > linksToDisplayWhenCollapsed && (
					<LinkIconStack
						sources={truncatedLinks}
						onClick={onExpandToggle}
						singleColumnLayout={singleColumnLayout}
					/>
				)}
			</Inline>
			<Grid
				rowGap="space.200"
				columnGap="space.400"
				xcss={cx(
					styles.relatedLinksGrid,
					singleColumnLayout ? styles.singleColumnLayout : styles.dualColumnLayout,
				)}
			>
				{linksToDisplay.map((link, index) => (
					<LinkCard key={index} url={link} onClick={onResourceClick} />
				))}
			</Grid>
		</>
	);
};
